import { invoke } from "@tauri-apps/api/core";
import {
  getWindowDisplayId,
  getWindowRoleSync,
} from "@/desktop/use-window-role";
import { getCurrentOwnerEpoch } from "@/lib/owner-state-client";
import { isTauri } from "@/lib/is-tauri";
import type {
  AgentPresentation,
  AgentPresentationStep,
  PresentationAppReason,
} from "@/lib/agent-presentation-types";
import { resolvePresentationInteraction } from "@/lib/agent-presentation-interaction";
import { presentationPaintGeometry } from "@/lib/presentation-paint-geometry";
import {
  rendersInCurrentWindow,
  tracePresentationDelivery,
} from "./agent-presentation-delivery-trace";
export { rendersInCurrentWindow } from "./agent-presentation-delivery-trace";
import { rememberPresentationComponent } from "./presentation-local-receipts";
import {
  paintFailureReason,
  performedByTheApplication,
  visibleStepAnnotation,
  visualIsPainted,
} from "./agent-presentation-paint-check";

type AcknowledgementState = "rendered" | "completed";

async function acknowledge(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
  state: AcknowledgementState,
  signal: AbortSignal,
): Promise<boolean> {
  const trace = (outcome: string) =>
    tracePresentationDelivery(presentation, step, state, outcome);
  if (!isTauri() || !rendersInCurrentWindow(presentation, step)) {
    trace(isTauri() ? "skipped_window" : "skipped_non_native");
    return true;
  }
  trace("paint_wait_started");
  // An action the application performs on itself does not wait on the
  // decoration: it looks once, over one frame, and goes ahead either way. The
  // cursor beside a fill is there to be glanced at, not a precondition - the
  // press goes through UI Automation whether or not it painted, and the user
  // asked for the form to be filled, not watched. Measured: waiting stopped
  // every `then` chain on a 36-field form after one to three fields, and once
  // that was lifted, every other hop of a 32-field form still sat out the full
  // wait twice - about 2.3s of the 3s such a field took. Pointing, guides and
  // anything that moves the real pointer still refuse to act unseen.
  const appPerformed = performedByTheApplication(presentation, step);
  const painted = await visualIsPainted(
    presentation,
    step,
    signal,
    appPerformed ? 1 : undefined,
  );
  if (signal.aborted) {
    trace("aborted");
    return false;
  }
  if (!painted && appPerformed) {
    trace("paint_late_semantic_action");
  } else if (!painted) {
    trace("paint_failed");
    if (presentation.requestedAcknowledgement !== "accepted") {
      await reportPaintFailure(
        presentation,
        step,
        paintFailureReason(presentation, step),
      );
    }
    return false;
  }
  if (state === "rendered")
    rememberPresentationComponent(presentation, step, "visual", "rendered");
  if (presentation.requestedAcknowledgement === "accepted") {
    trace("skipped_accepted_only");
    return true;
  }
  const displayId = getWindowDisplayId();
  if (displayId == null) {
    trace("skipped_missing_display");
    return false;
  }
  const hasInteraction =
    step.kind === "click" ||
    step.kind === "hover" ||
    step.kind === "drag" ||
    step.kind === "scroll" ||
    step.kind === "paste" ||
    step.kind === "key" ||
    step.kind === "type";
  const interaction = hasInteraction
    ? resolvePresentationInteraction(presentation, step)
    : null;
  if (hasInteraction && !interaction) {
    await reportPaintFailure(
      presentation,
      step,
      "interaction_target_unresolved",
    );
    return false;
  }
  try {
    trace("ipc_attempted");
    await invoke("acknowledge_presentation_delivery", {
      acknowledgement: {
        presentationId: presentation.presentationId,
        executionId: presentation.executionId ?? "",
        stepId: step.step_id,
        playbackAttempt: presentation.playbackAttempt ?? 1,
        displayId,
        state,
        ownerEpoch: getCurrentOwnerEpoch(),
        ...(state === "rendered"
          ? {
              paintGeometry: presentationPaintGeometry(
                presentation,
                step,
                visibleStepAnnotation(presentation, step),
              ),
            }
          : {}),
        ...(interaction
          ? {
              interactionAction: interaction.action,
              interactionParams: interaction.params,
            }
          : {}),
      },
    });
    trace("ipc_succeeded");
    return true;
  } catch (error) {
    trace("ipc_rejected");
    const reason =
      error instanceof Error
        ? error.message
        : typeof error === "string"
          ? error
          : "";
    await reportPaintFailure(
      presentation,
      step,
      reason || "acknowledgement_rejected",
    );
    return false;
  }
}

async function reportPaintFailure(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
  reason: string,
): Promise<void> {
  const displayId = getWindowDisplayId();
  if (displayId == null) {
    tracePresentationDelivery(
      presentation,
      step,
      "failure_report",
      "skipped_missing_display",
    );
    return;
  }
  tracePresentationDelivery(
    presentation,
    step,
    "failure_report",
    "ipc_attempted",
  );
  await invoke("fail_presentation_delivery", {
    failure: {
      presentationId: presentation.presentationId,
      executionId: presentation.executionId ?? "",
      stepId: step.step_id,
      playbackAttempt: presentation.playbackAttempt ?? 1,
      displayId,
      ownerEpoch: getCurrentOwnerEpoch(),
      reason,
    },
  }).then(
    () =>
      tracePresentationDelivery(
        presentation,
        step,
        "failure_report",
        "ipc_succeeded",
      ),
    () =>
      tracePresentationDelivery(
        presentation,
        step,
        "failure_report",
        "ipc_rejected",
      ),
  );
}

export function failPresentationStep(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
  reason: string,
): Promise<void> {
  if (!isTauri() || !rendersInCurrentWindow(presentation, step)) {
    tracePresentationDelivery(
      presentation,
      step,
      "failure_report",
      isTauri() ? "skipped_window" : "skipped_non_native",
    );
    return Promise.resolve();
  }
  return reportPaintFailure(presentation, step, reason);
}

export function acknowledgePresentationPaint(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
  signal: AbortSignal,
): Promise<boolean> {
  return acknowledge(presentation, step, "rendered", signal);
}

export function acknowledgePresentationCompletion(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
  signal: AbortSignal,
): Promise<boolean> {
  if (
    presentation.requestedAcknowledgement !== "completed" ||
    presentation.steps[presentation.steps.length - 1]?.step_id !== step.step_id
  ) {
    return Promise.resolve(true);
  }
  return acknowledge(presentation, step, "completed", signal);
}

/** With an app reason it ends as the app's failure, never as the user's stop. */
export function cancelPresentationDelivery(
  presentation: AgentPresentation,
  reason?: PresentationAppReason,
): void {
  if (!isTauri() || presentation.requestedAcknowledgement === "accepted") {
    return;
  }
  if (getWindowRoleSync() !== "owner") return;
  void invoke("cancel_presentation_delivery", {
    presentationId: presentation.presentationId,
    executionId: presentation.executionId ?? "",
    ownerEpoch: getCurrentOwnerEpoch(),
    reason: reason ?? null,
  }).catch(() => {});
}
